import { ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown, MoreHorizontal, Eye, User, Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Appointment } from '@/lib/api-client'
import { formatCurrency } from '@/lib/utils'

const BRANCH_COLORS: Record<string, string> = {
  'Shah Alam': '#f59e0b',
  Putrajaya: '#4a8fe8',
  Bangi: '#10b981',
  'Kuala Lumpur': '#ec4899',
}

// HQ closer set — same as backend constant. Drives 🎯 vs 🏥 badge.
const CLOSER_USERNAMES = ['Azwa', 'Karmila', 'Liyana', 'Raja']

const STATUS_STYLE: Record<Appointment['status'], { bg: string; text: string; muted?: boolean }> = {
  turn_up: { bg: '#10b981', text: 'white' },
  // DNTU is the noisy default — visually de-emphasised (handoff §spec)
  did_not_turn_up: { bg: '#525860', text: '#cbd5e1', muted: true },
  cancelled: { bg: '#ef4444', text: 'white' },
}

const STATUS_LABEL: Record<Appointment['status'], string> = {
  turn_up: 'Turn Up',
  did_not_turn_up: 'No-flip',
  cancelled: 'Cancelled',
}

interface AppointmentColumnsProps {
  onView: (appointment: Appointment) => void
  onViewPatient?: (patientId: number) => void
  onViewInvoice?: (invoiceNo: string) => void
}

function formatDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  return {
    date: d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', timeZone: 'Asia/Kuala_Lumpur' }),
    time: d.toLocaleTimeString('en-MY', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kuala_Lumpur',
    }),
  }
}

export function getAppointmentColumns({ onView, onViewPatient, onViewInvoice }: AppointmentColumnsProps): ColumnDef<Appointment>[] {
  return [
    // Date / Time
    {
      accessorKey: 'appointmentAt',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Date / Time
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const { date, time } = formatDateTime(row.getValue('appointmentAt') as string)
        const isTask = row.original.isTask
        return (
          <div className={isTask ? 'opacity-60' : ''}>
            {isTask && <Phone className="inline w-3 h-3 mr-1 text-text-muted" />}
            <span className="font-medium">{date}</span>
            <div className="text-xs text-muted-foreground">{time}</div>
          </div>
        )
      },
    },

    // Doctor
    {
      accessorKey: 'doctor',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Doctor
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const v = row.getValue('doctor') as string | null
        return v ? <span>{v}</span> : <span className="text-muted-foreground">-</span>
      },
    },

    // Patient
    {
      accessorKey: 'patientName',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Patient
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const name = (row.original.patientLinkedName ?? row.getValue('patientName')) as string | null
        const pno = row.original.pno
        if (!name) return <span className="text-muted-foreground italic">{row.original.isTask ? 'Task placeholder' : '(no name)'}</span>
        return (
          <div>
            <div className="font-medium">{name}</div>
            {pno && <div className="text-xs text-muted-foreground">{pno}</div>}
          </div>
        )
      },
    },

    // Patient Type
    {
      accessorKey: 'patientType',
      header: 'Type',
      cell: ({ row }) => {
        const v = row.getValue('patientType') as string | null
        if (!v) return <span className="text-muted-foreground">-</span>
        const isNew = v === 'New'
        return (
          <Badge style={{ background: isNew ? '#3b82f6' : '#8b5cf6', color: 'white' }}>
            {isNew ? 'New' : 'Existing'}
          </Badge>
        )
      },
    },

    // Status (DNTU visually de-emphasised per handoff)
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as Appointment['status']
        const style = STATUS_STYLE[status]
        const comment = row.original.cancelComment
        return (
          <div>
            <Badge
              style={{ background: style.bg, color: style.text }}
              className={style.muted ? 'opacity-70' : ''}
            >
              {STATUS_LABEL[status]}
            </Badge>
            {comment && <div className="text-xs text-muted-foreground mt-0.5">{comment}</div>}
          </div>
        )
      },
    },

    // Created By (with closer/in-clinic icon)
    {
      accessorKey: 'createdBy',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Created By
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const v = row.getValue('createdBy') as string | null
        if (!v) return <span className="text-muted-foreground">-</span>
        const isCloser = CLOSER_USERNAMES.includes(v)
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-xs">{isCloser ? '🎯' : '🏥'}</span>
            <span>{v}</span>
          </div>
        )
      },
    },

    // Invoice
    {
      accessorKey: 'invoiceNo',
      header: 'Invoice',
      cell: ({ row }) => {
        const no = row.getValue('invoiceNo') as string | null
        const amt = row.original.invoiceAmount
        if (!no) return <span className="text-muted-foreground">-</span>
        return (
          <div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onViewInvoice?.(no)
              }}
              className="text-info hover:underline font-mono text-xs"
            >
              {no}
            </button>
            {amt && <div className="text-xs">{formatCurrency(parseFloat(amt))}</div>}
          </div>
        )
      },
    },

    // Visit Reason
    {
      accessorKey: 'visitReasonCategory',
      header: 'Visit Reason',
      cell: ({ row }) => {
        const cat = row.getValue('visitReasonCategory') as string | null
        const detail = row.original.visitReasonDetail
        if (!cat) return <span className="text-muted-foreground">-</span>
        return (
          <div>
            <Badge variant="outline" className="text-xs">{cat}</Badge>
            {detail && <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[180px]" title={detail}>{detail}</div>}
          </div>
        )
      },
    },

    // Branch (hidden by default if branch already filtered)
    {
      accessorKey: 'branchName',
      header: 'Branch',
      cell: ({ row }) => {
        const branch = row.getValue('branchName') as string | null
        if (!branch) return null
        const color = BRANCH_COLORS[branch] ?? '#6b7280'
        return <Badge style={{ background: color }} className="text-white">{branch}</Badge>
      },
    },

    // Time In (hidden by default)
    {
      accessorKey: 'timeIn',
      header: 'Time In',
      cell: ({ row }) => {
        const v = row.getValue('timeIn') as string | null
        return v ? <span className="text-xs">{v}</span> : <span className="text-muted-foreground">-</span>
      },
    },

    // NRIC (hidden by default)
    {
      accessorKey: 'patientNric',
      header: 'NRIC',
      cell: ({ row }) => {
        const v = row.getValue('patientNric') as string | null
        return v ? <span className="font-mono text-xs">{v}</span> : <span className="text-muted-foreground">-</span>
      },
    },

    // Actions
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const apt = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 w-9 p-0" onClick={(e) => e.stopPropagation()}>
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-[13px]">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onView(apt)} className="text-[13px]">
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              {apt.patientId && onViewPatient && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onViewPatient(apt.patientId!)} className="text-[13px]">
                    <User className="mr-2 h-4 w-4" />
                    View Patient
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}

// Default-hidden columns — operator can toggle in
export const APPOINTMENT_HIDDEN_COLUMNS = ['timeIn', 'patientNric'] as const
